











































import { Vue, Component, Prop } from 'vue-property-decorator';
import {
    HeroBannerUspItemViewModel,
    HeroBannerWithUspBlockViewModel,
    SimpleUspBlockViewModel
} from '@/types/contentServerContract';
import SmartHeadline from '@/core/ui-components/SmartHeadline.vue';
import { hexToRgb, isValidHexColor } from '@/core/color/color.service';
import VimeoPlayer from '@vimeo/player';
import tailwind from '@/styling/tailwind.config';
import breakpointState from '@/core/responsive/breakpoints/breakpointsState.observable';
import { MAX_SM } from '@/core/config/breakpointConfig';
import SimpleUniqueSellingPointsBlock from '@/project/app/blocks/SimpleUniqueSellingPointsBlock.vue';

@Component({
    components: { SimpleUniqueSellingPointsBlock, SmartHeadline }
})
export default class HeroBannerUniqueSellingPointsBlock extends Vue {
    @Prop({
        required: true,
        type: Object
    }) content!: HeroBannerWithUspBlockViewModel;

    @Prop({
        required: true,
        type: Boolean
    }) isFirstBlock!: boolean;

    video: VimeoPlayer | null = null;
    loadingVideo: boolean = true;
    isBackgroundVideo: boolean = true;
    $refs!: {
        videoMount?: HTMLElement
    }

    get simpleUniqueSellingPointsBlockContent():SimpleUspBlockViewModel {
        return {
            appendToPreviousBlock: true,
            showBlockOnMobile: this.content.showUspItemsOnMobile,
            backgroundColor: this.uniqueSellingPointsBackgroundColor,
            textColor: this.uniqueSellingPointsColor,
            uniqueSellingPointItems: [...this.content.uspItems]
        };
    }

    get videoMountStyle(): Partial<CSSStyleDeclaration> {
        // Set initial aspect ratio on div while loading video. 1366/456 is the aspect ratio of mostly used video - currently
        // @ts-ignore
        return this.hasVideo && this.loadingVideo ? { aspectRatio: 1366 / 456 } : {};
    }

    get hasVideo(): boolean {
        return !!(this.content?.vimeoVideoId || this.content.mobileVimeoVideoId);
    }

    mounted() {
        if (this.hasVideo) {
            // Init desktop or mobile video, video won't change / re-init if resized across md breakpoint. Fallback to one of them.
            let vimeoVideoId: string = this.isMobile ? this.content.mobileVimeoVideoId : this.content.vimeoVideoId;
            if (!vimeoVideoId) {
                vimeoVideoId = this.content.vimeoVideoId || this.content.mobileVimeoVideoId;
            }

            this.loadingVideo = this.hasVideo;
            this.video = null;
            const options = {
                id: ensureId(vimeoVideoId),
                autoplay: this.isBackgroundVideo,
                loop: this.isBackgroundVideo,
                muted: this.isBackgroundVideo,
                controls: false,
                title: false,
                byline: false,
                portrait: false,
                responsive: true,
                autopause: !this.isBackgroundVideo // Ensure that all videos on a page will autoplay
            };
            // eslint-disable-next-line no-new
            this.video = new VimeoPlayer(this.$refs.videoMount as HTMLElement, options) as VimeoPlayer;

            this.video.on('loaded', (vimeoVideoId) => {
                this.loadingVideo = false;
            });
        }

        function ensureId(idOrUrl: string): number {
            const idParts = idOrUrl.split(/(\\|\/)/);
            let id = idOrUrl;
            if (idParts.length > 1) {
                id = idParts[idParts.length - 1];
            }
            return parseInt(id, 10);
        }
    }

    get uspStyle(): Partial<CSSStyleDeclaration> | null {
        const style: Partial<CSSStyleDeclaration> = {};
        if (this.uniqueSellingPointsColor) {
            style.color = this.uniqueSellingPointsColor;
        }
        if (this.uniqueSellingPointsBackgroundColor) {
            style.backgroundColor = this.uniqueSellingPointsBackgroundColor;
        }
        return style;
    }

    get uniqueSellingPointsBackgroundColor() {
        return '#262422'; // Hardcoded for now
    }

    get uniqueSellingPointsColor() {
        return '#ffffff'; // Hardcoded for now
    }

    get shimStyle(): Partial<CSSStyleDeclaration> | null {
        if (this.content.shimColor && this.isColorDefined(this.content.shimColor)) {
            return {
                backgroundColor: this.hexToRgbWithDefault(this.content?.shimColor, 0.3)
                // opacity: '0.3',
            };
        }
        return null;
    }

    get contentClasses(): string {
        return (this.content.vimeoVideoId || this.content.mobileVimeoVideoId) || (this.content.backgroundImage && this.content.backgroundImage.url) ? 'absolute inset-0' : 'relative py-60';
    }

    colorStyle(hexColor: string): string {
        const EETBlack = tailwind.theme.colors.black;
        return hexColor ? this.hexToRgbWithDefault(hexColor) : EETBlack;
        // Reuse from ImageTextVideoBlock.vue, but we might not have contentBackgroundColor in this content.
        // return this.contentBackgroundColor === 'white' ? EETBlack : hexColor ? this.hexToRgbWithDefault(hexColor) : EETBlack;
    }

    hexToRgbWithDefault(hex: string, alpha?: number): string {
        if (!isValidHexColor(hex)) return tailwind.theme.colors.black;
        return hexToRgb(hex, alpha);
    }

    isColorDefined(color: string): boolean {
        return color?.length === 7 ?? false; // #233445
    }

    get headerStyle(): Partial<CSSStyleDeclaration> {
        return { color: this.colorStyle(this.content.headerColor) };
    }

    get subHeaderStyle(): Partial<CSSStyleDeclaration> {
        return { color: this.colorStyle(this.content.headerColor) };
    }

    get isMobile() {
        return breakpointState.isBreakpointActive(MAX_SM);
    }

    get uspItems(): { content: HeroBannerUspItemViewModel }[] {
        return this.content.uspItems || [];
    }
}
